import React, { useEffect, useState } from 'react';
import { Gift, X, ArrowRight } from 'lucide-react';

const LS_DISMISS_KEY = 'neurowave_referral_banner_dismissed_v1';
const DISMISS_HOURS = 72; // re-show after 3 days
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Slim, dismissible banner that prompts authenticated users to invite friends.
 * Hidden when:
 *   - No user signed in
 *   - User has already maxed out their referrals
 *   - User dismissed in the past DISMISS_HOURS
 */
const ReferralBanner = ({ user, onOpenPanel }) => {
  const [info, setInfo] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!user) return;
    // Respect the dismissal window
    let dismissedAt = 0;
    try { dismissedAt = Number(localStorage.getItem(LS_DISMISS_KEY) || 0); } catch { /* ignore */ }
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_HOURS * 3600 * 1000) return;

    fetch(`${API}/referral/info`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data) return;
        setInfo(data);
        if (data.successful_referrals < data.max_referrals) {
          setVisible(true);
        }
      })
      .catch(() => { /* ignore — banner is non-essential */ });
  }, [user]);

  const dismiss = () => {
    try { localStorage.setItem(LS_DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setVisible(false);
  };

  if (!visible || !info) return null;

  const remaining = info.max_referrals - info.successful_referrals;
  const maxDays = info.max_referrals * info.bonus_days_per_referral;

  return (
    <div
      className="relative group mx-auto max-w-3xl mb-4 rounded-2xl border border-emerald-500/20 bg-gradient-to-r from-emerald-500/[0.08] via-cyan-500/[0.08] to-purple-500/[0.08] px-4 py-3 sm:px-5"
      data-testid="referral-banner"
    >
      <button
        onClick={dismiss}
        className="absolute top-2 right-2 text-white/30 hover:text-white/60 transition"
        aria-label="Dismiss"
        data-testid="referral-banner-dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
      <div className="flex items-center gap-3 pr-6">
        <div className="shrink-0 w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-emerald-500/20">
          <Gift className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-medium leading-tight">
            {info.successful_referrals === 0
              ? `Invite friends, get up to ${maxDays} bonus premium days`
              : `You've earned ${info.bonus_days_earned} bonus days — ${remaining} more ${remaining === 1 ? 'invite' : 'invites'} to max out`}
          </p>
          <p className="text-white/50 text-xs mt-0.5">
            Both of you get {info.bonus_days_per_referral} days added to your premium trial.
          </p>
        </div>
        <button
          onClick={onOpenPanel}
          className="shrink-0 hidden sm:inline-flex items-center gap-1 text-xs font-semibold text-white bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-700 hover:to-cyan-700 rounded-lg px-3 py-2 transition shadow-md"
          data-testid="referral-banner-cta"
        >
          Share <ArrowRight className="w-3 h-3" />
        </button>
      </div>
      {/* Mobile CTA */}
      <button
        onClick={onOpenPanel}
        className="sm:hidden mt-2 w-full text-xs font-semibold text-white bg-gradient-to-r from-emerald-600 to-cyan-600 rounded-lg py-2"
        data-testid="referral-banner-cta-mobile"
      >
        Share your code →
      </button>
    </div>
  );
};

export default ReferralBanner;
